import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import clinicService from '../../services/clinic.service'
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile'

const REDIRECT_SECONDS = 5

const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return null
  const trimmed = url.trim()
  if (!trimmed) return null
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

const FeedbackUnsat = ({ clinic, patientID }) => {
  const [done, setDone] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)
  const turnstileRequired = isTurnstileConfigured()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm()

  const websiteUrl = normalizeUrl(clinic?.website)

  useEffect(() => {
    if (!done || !websiteUrl) return

    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    const timeout = setTimeout(() => {
      window.location.href = websiteUrl
    }, REDIRECT_SECONDS * 1000)

    return () => {
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [done, websiteUrl])

  const onSubmit = (data) => {
    if (submitting) return
    if (turnstileRequired && !turnstileToken) {
      setError('Please complete the verification challenge.')
      return
    }
    setSubmitting(true)
    setError(null)

    clinicService
      .addComment(clinic.ID, {
        ...data,
        patientID,
        turnstileToken: turnstileToken || null,
      })
      .then(() => setDone(true))
      .catch(() => {
        setError('Unable to send your comment. Please try again.')
        setTurnstileToken('')
        setTurnstileResetKey((k) => k + 1)
      })
      .finally(() => setSubmitting(false))
  }

  if (done) {
    return (
      <section className="rep-panel">
        <div className="rep-done">
          <div className="rep-done-mark" aria-hidden="true">
            <i className="bi bi-check-lg" />
          </div>
          <h2 className="rep-title">Thank you for your feedback</h2>
          <p className="rep-copy" style={{ marginBottom: websiteUrl ? '1rem' : 0 }}>
            Your comments have been received. We take this seriously and will use
            it to improve.
          </p>
          {websiteUrl && (
            <p className="rep-redirect-note">
              Redirecting you to our website in {countdown}{' '}
              {countdown === 1 ? 'second' : 'seconds'}…{' '}
              <a href={websiteUrl}>Go now</a>
            </p>
          )}
        </div>
      </section>
    )
  }

  return (
    <section className="rep-panel">
      <p className="rep-kicker">Tell us more</p>
      <h2 className="rep-title">What could we have done better?</h2>
      <p className="rep-copy">
        Your honesty helps {clinic.name} improve. Share as much detail as you are
        comfortable with.
      </p>

      <form className="rep-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="rep-form-row">
          <div className="rep-field">
            <label htmlFor="rep-first-name">First name</label>
            <input
              id="rep-first-name"
              type="text"
              autoComplete="given-name"
              {...register('firstName')}
            />
          </div>
          <div className="rep-field">
            <label htmlFor="rep-last-name">Last name</label>
            <input
              id="rep-last-name"
              type="text"
              autoComplete="family-name"
              {...register('lastName')}
            />
          </div>
        </div>

        <div className="rep-field">
          <label htmlFor="rep-notes">Comment</label>
          <textarea
            id="rep-notes"
            rows="5"
            {...register('notes', { required: true })}
          />
          {errors.notes ? (
            <span className="rep-field-error">Please share a short comment.</span>
          ) : null}
        </div>

        {turnstileRequired ? (
          <div className="rep-field">
            <Turnstile
              resetKey={turnstileResetKey}
              onSuccess={(token) => setTurnstileToken(token)}
              onExpire={() => setTurnstileToken('')}
              onError={() => setTurnstileToken('')}
            />
          </div>
        ) : null}

        {error ? <p className="rep-error">{error}</p> : null}

        <div className="rep-actions">
          <button
            type="button"
            className="rep-btn rep-btn--ghost"
            onClick={() => window.location.reload()}
            disabled={submitting}
          >
            Back
          </button>
          <button
            type="submit"
            className="rep-btn rep-btn--primary"
            disabled={submitting || (turnstileRequired && !turnstileToken)}
          >
            {submitting ? 'Sending…' : 'Submit feedback'}
          </button>
        </div>
      </form>
    </section>
  )
}

export default FeedbackUnsat
